/* import __COLOCATED_TEMPLATE__ from './localized-video.hbs.js'; */
var _dec, _dec2, _class, _class2, _descriptor, _descriptor2, _descriptor3, _class3;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import Component from '@ember/component';
import { inject as service } from '@ember/service';
import { computed } from '@ember/object';
import { tagName } from '@ember-decorators/component';
import Logger from '@square/dashboard-utilities/utils/logger';
import Enum from '@square/dashboard-utilities/utils/enum';
import environment from '@square/environment-utils/environment';
import * as Sentry from '@sentry/ember';

/**
 * A hash of all the default locales, one per country. Please keep this alphabetized by key!
 * @type {Object}
 */
const DefaultLocale = {
  AU: 'en-AU',
  CA: 'en-CA',
  ES: 'es-ES',
  FR: 'fr-FR',
  GB: 'en-GB',
  IE: 'en-IE',
  JP: 'ja-JP',
  US: 'en-US'
};

/**
 * `LocalizedVideo` is designed to be used as drop-in replacement for `VideoPlayer` to add support for video
 * localization.
 *
 * For an in-depth overview of this localization system (originally pitched as a helper), check out the original proposal:
 * https://docs.google.com/document/d/1hvOyNsDDxZKpZn8QFYxe3hDKST5ga0oIczs8NV_GA3c/edit#
 *
 * @example
 * <LocalizedVideo @directory="/01-dashboard-shared/videos/retail"
 *                 @videoName="retail-welcome-modal-register"
 *                 @description="The Retail welcome modal register" />
 *
 * @param {string} directory - The directory the video is located in.
 * @param {string} videoName - The name of the video.
 * @param {string} [extension='mp4'] - The extension to be used when generating the video path.
 * @param {string} [type="video/mp4"] Declare the type of the source for the optimization (optional)
 * @param {string} description Video alt text
 * @param {string} poster Poster image src. Shows while the video is loading
 * @param {string} onPlay Send this action when the video plays
 * @param {string} onPause Send this action when the video pauses
 * @param {string} onEnd Send this action when the video ends
 * @param {string} onTimeUpdate Send this action on video time update.
 * @param {Boolean} [autoplay=false] Autoplay video (optional)
 * @param {Boolean} [loop=false] Loop video (optional)
 * @param {Boolean} [playsinline=false] Play video inline in mobile browsers (optional)
 * @param {Boolean} [canInteract=true] Set this to false if you need something akin to a background video where you don't want the user to be able to interact with it
 * @param {boolean} [allowFallbackToUs=true] - If `true`, `LocalizedVideo` will choose the `en-US` locale as a final
 *   fallback to use if all else fails. Disable this in situations where showing `en-US` content might not be desirable.
 * @param {boolean} [isExternal=false] - Set to `true` when specifying a URL for videos directly uploaded to third party
 *   storage solutions, such as S3. For further context, search Dashboard for usages.
 */
let LocalizedVideo = (_dec = tagName(''), _dec2 = computed('bestFitLocale', 'isExternal'), _dec(_class = (_class2 = (_class3 = class LocalizedVideo extends Component {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "assetMap", _descriptor, this);
    _initializerDefineProperty(this, "userInfo", _descriptor2, this);
    _initializerDefineProperty(this, "i18n", _descriptor3, this);
    _defineProperty(this, "isLoading", true);
    _defineProperty(this, "bestFitLocale", null);
    _defineProperty(this, "directory", null);
    _defineProperty(this, "videoName", null);
    _defineProperty(this, "extension", 'mp4');
    _defineProperty(this, "allowFallbackToUS", true);
    _defineProperty(this, "isExternal", false);
    _defineProperty(this, "autoplay", false);
    _defineProperty(this, "loop", false);
    _defineProperty(this, "playsinline", false);
    _defineProperty(this, "canInteract", true);
    _defineProperty(this, "queryCache", {});
  }
  get isLoggingEnabled() {
    const queryParam = new URLSearchParams(window.location.search).get('logLocalizedVideo');
    return queryParam === 'true';
  }
  get src() {
    return this.createLocalizedVideoPath(this.bestFitLocale, !this.isExternal);
  }

  /**
   * Determines the "best fit" locale according to the standardized localization algorithm.
   *
   * The localization algorithm:
   *   1. `LocalizedVideo` will try the locale created by joining the user’s preferred language code and the user’s country code
   *      (this is the country that is associated with their account): <preferred language code>-<current country code>.
   *      The language code will be pulled from the user’s browser, and the country code will be pulled from the user’s
   *      account. If an video exists for this, serve that.
   *   2. If #1 fails, `LocalizedVideo` will check if a default video variant exists for the user’s current country, which is
   *      determined by checking the default locale list (hash of all default locales, one per country). If an video exists
   *      for this, serve that.
   *   3. If the checks above fail, then `LocalizedVideo` will attempt to serve an video with the fallback locale of en-US
   *      if allowed.
   *
   * If a best-fit locale could not be determined, `bestFitLocale` will remain `null`, and `LocalizedVideo` will render a
   * missing video placeholder.
   */
  async findBestFitLocale() {
    const browserLocaleParts = this.userInfo.browserLanguage.split('-');

    // 1) User's preferred language code + user's current country code.
    const preferredLocale = `${browserLocaleParts[0]}-${this.userInfo.countryCode}`;
    if (await this.doesAssetExistWithLocale(preferredLocale)) {
      this.logSelectionOfLocale(LocalizedVideo.LocaleType.preferred, preferredLocale);
      return preferredLocale;
    }

    // 2) Default locale given current users country code.
    const defaultLocale = DefaultLocale[this.userInfo.countryCode];
    if (await this.doesAssetExistWithLocale(defaultLocale)) {
      this.logSelectionOfLocale(LocalizedVideo.LocaleType.default, defaultLocale);
      return defaultLocale;
    }

    // 3) Fallback locale if allowed.
    if (this.allowFallbackToUS) {
      this.logSelectionOfLocale(LocalizedVideo.LocaleType.fallback, DefaultLocale.US);
      return DefaultLocale.US;
    }
    return null;
  }
  didReceiveAttrs() {
    super.didReceiveAttrs();
    if (this.assetMap.prepend !== '' && this.directory[0] === '/') {
      this.directory = this.directory.slice(1);
    }

    // Initiate async loading of the locale. In production this would all be instantaneous because no fetch requests
    // would be made to the local server, and all methods would return already-resolved promises.
    this.findBestFitLocale().then(locale => {
      if (this.isDestroyed || this.isDestroying) {
        return;
      }
      this.set('isLoading', false);
      if (locale) {
        this.set('bestFitLocale', locale);
      } else {
        const message = `A best-fit locale could not be found for { countryCode: ${this.userInfo.countryCode}, browserLanguage: ${this.userInfo.browserLanguage} }`;
        Sentry.captureMessage('A best-fit locale could not be found');
        Logger.warn(message);
      }
    });
  }
  createLocalizedVideoPath(locale, shouldIncludeFingerprint = true) {
    const localizedVideoPath = `${this.directory}/${this.createVideoNameWithLocale(locale)}`;
    return shouldIncludeFingerprint ? this.assetMap.resolve(localizedVideoPath) : localizedVideoPath;
  }
  createVideoNameWithLocale(locale) {
    return `${this.videoName}--${locale}.${this.extension}`;
  }

  /**
   * Checks the `assetMap` service to see if an asset with the specified locale exists.
   * @param {?string} locale - A locale used to check for the existence of an asset.
   * @returns {Promise} A `Promise` whose value will indicate whether or not the asset exists in the asset map.
   */
  doesAssetExistWithLocale(locale) {
    if (!locale) {
      return Promise.resolve(false);
    }
    const fullVideoPathWithoutFingerprint = this.createLocalizedVideoPath(locale, false);

    // NB(tbiesecker): Due to the fact that the `assetMap` service is disabled during development, a workaround is
    // needed to simulate the functionality that would otherwise have been provided by the `assetMap` service. In
    // development environments, we simply query the local server for the asset to determine if it exists or not. This
    // should add almost no overhead and will allow developers to see how the `LocalizedVideo` component would behave in
    // production. Note, however, that synchronous network calls are deprecated, and a better solution should be found.
    // Ideally, we would figure out how to enable the `assetMap` service in local development and therefore remove the
    // need for any workaround.
    // TODO(RETAIL-11394): Enable the assetMap service in local development and remove this workaround.
    if (this.isExternal || !environment().isTesting && environment().isDevelopment) {
      const path = this.isExternal ? fullVideoPathWithoutFingerprint : `/${fullVideoPathWithoutFingerprint}`;
      return this.queryServerForExistenceOfAssetWithPath(path);
    }
    return Promise.resolve(Boolean(this.assetMap.fullMap?.[fullVideoPathWithoutFingerprint]));
  }

  /**
   * @param {LocalizedVideo.LocaleType} localeType - The type of locale.
   * @param {string} locale - The locale that was selected.
   */
  logSelectionOfLocale(localeType, locale) {
    if (this.isLoggingEnabled) {
      Logger.info(`LocalizedVideo selected: { localeType: ${localeType}, locale: ${locale}, video: ${this.videoName} }`);
    }
  }
  async queryServerForExistenceOfAssetWithPath(path) {
    if (this.queryCache[path] !== undefined) {
      return this.queryCache[path];
    }
    const response = await fetch(path);
    this.queryCache[path] = response.status === 200;
    return this.queryCache[path];
  }
}, _defineProperty(_class3, "LocaleType", new Enum(['default', 'preferred', 'fallback'])), _class3), _descriptor = _applyDecoratedDescriptor(_class2.prototype, "assetMap", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class2.prototype, "userInfo", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class2.prototype, "i18n", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class2.prototype, "src", [_dec2], Object.getOwnPropertyDescriptor(_class2.prototype, "src"), _class2.prototype), _class2)) || _class);
export { LocalizedVideo as default };